import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Crosshair } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, DataTable, Td, FormDialog, Field, TextInput, TextArea, SelectInput, ActionButton, EmptyState } from "../components/common";

const EMPTY = { name: "", customer_id: "", wells: "", address: "", lat: "", lng: "", notes: "" };
const parseWells = (raw) => raw.split(/[\n,]/).map((w) => w.trim()).filter(Boolean);

export default function Locations() {
  const { user } = useAuth();
  const canDelete = ["admin", "shop_manager", "dispatcher"].includes(user?.role);
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);

  const load = () => api.get("/locations").then((r) => setLocations(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const customerOptions = [...customers].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name }));

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ name: l.name || "", customer_id: l.customer_id || "", wells: (l.wells || []).join("\n"), address: l.address || "", lat: l.lat ?? "", lng: l.lng ?? "", notes: l.notes || "" });
    setOpen(true);
  };

  const fillGps = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported by this browser"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })); toast.success("Coordinates captured from this device"); setLocating(false); },
      () => { toast.error("Location permission denied — enter coordinates manually"); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { name: form.name, customer_id: form.customer_id || null, wells: parseWells(form.wells) };
    if (form.address) payload.address = form.address;
    if (form.notes) payload.notes = form.notes;
    if (form.lat !== "" && form.lng !== "") { payload.lat = Number(form.lat); payload.lng = Number(form.lng); }
    try {
      if (editing) { await api.patch(`/locations/${editing.id}`, payload); toast.success(`Location ${form.name} updated`); }
      else { await api.post("/locations", payload); toast.success(`Location ${form.name} added`); }
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete location ${l.name}? This cannot be undone.`)) return;
    try { await api.delete(`/locations/${l.id}`); toast.success(`Location ${l.name} deleted`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="locations-page">
      <PageHeader title="Locations" subtitle="Well sites per customer — each location holds one or more wells" testId="locations-header">
        <ActionButton testId="create-location-button" onClick={openCreate}><Plus size={16} /> Add Location</ActionButton>
      </PageHeader>

      {locations.length === 0 ? (
        <EmptyState message="No locations saved yet" testId="locations-empty" />
      ) : (
        <DataTable
          testId="locations-table"
          columns={["Location", "Customer", "Wells", "Address", "GPS", ""]}
          rows={locations}
          renderRow={(l) => (
            <tr key={l.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`location-row-${l.id}`}>
              <td className="px-4 py-3 border-b border-slate-800/60 font-medium text-slate-100 whitespace-nowrap">{l.name}</td>
              <Td className="text-slate-400">{custName(l.customer_id)}</Td>
              <Td className="text-slate-300 max-w-md">
                {(l.wells || []).length ? <span className="text-xs">{l.wells.join(", ")}</span> : <span className="text-slate-600">—</span>}
              </Td>
              <Td className="text-slate-400 max-w-xs truncate">{l.address || "—"}</Td>
              <Td className="font-mono text-xs text-amber-400">{l.lat != null && l.lng != null ? `${l.lat.toFixed(5)}, ${l.lng.toFixed(5)}` : "—"}</Td>
              <Td>
                <div className="flex gap-1">
                  <button data-testid={`location-edit-${l.id}`} onClick={() => openEdit(l)} className="p-1.5 rounded text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors duration-200"><Pencil size={14} /></button>
                  {canDelete && <button data-testid={`location-delete-${l.id}`} onClick={() => remove(l)} className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-200"><Trash2 size={14} /></button>}
                </div>
              </Td>
            </tr>
          )}
        />
      )}

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Location" : "Add Location"} testId="location-form-dialog">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Customer">
            <SelectInput testId="location-customer-select" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required placeholder="Select customer" options={customerOptions} />
          </Field>
          <Field label="Location name">
            <TextInput testId="location-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. SOUTH MAN" />
          </Field>
          <Field label="Wells (one per line or comma-separated)">
            <TextArea testId="location-wells-input" rows={4} value={form.wells} onChange={(e) => setForm({ ...form, wells: e.target.value })} placeholder={"36-1-12HC 003 ALT\n36-1-12HC 001 ALT\n36-1-12HC 002 ALT"} />
          </Field>
          <Field label="Address">
            <TextInput testId="location-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, city, state" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Latitude"><TextInput testId="location-lat-input" type="number" step="any" min="-90" max="90" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="31.9973" /></Field>
            <Field label="Longitude"><TextInput testId="location-lng-input" type="number" step="any" min="-180" max="180" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="-102.0779" /></Field>
          </div>
          <button type="button" data-testid="location-use-gps-button" onClick={fillGps} disabled={locating} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-amber-400 hover:text-amber-300 transition-colors duration-200">
            <Crosshair size={14} /> {locating ? "Locating…" : "Use my current GPS position"}
          </button>
          <Field label="Notes"><TextArea testId="location-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Gate code, access instructions…" /></Field>
          <ActionButton testId="location-submit-button" type="submit" disabled={busy} className="w-full justify-center">{busy ? "Saving…" : editing ? "Save Changes" : "Add Location"}</ActionButton>
        </form>
      </FormDialog>
    </div>
  );
}
